import { ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { VEHICLE_POSITIONS_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

/**
 * Where the marked orders' vehicles are right now.
 *
 * Nothing is remembered between calls, unlike the routes and the reports beside them: a
 * route is what it was drawn as and a report is what was reported, but a position is
 * only true for the moment it was read. Marking the same order again asks again.
 *
 * What the map says about a vehicle is more than the position endpoint knows - who was
 * driving it and which order it is marked for come off the order itself - so the two are
 * put together here, and the panel is handed one record per vehicle.
 */
export function useWorkOrderVehiclePositions () {
  const isFetching = ref(false)

  const fetchPositions = async (vehicleIds) => {
    const { data } = await api(WINTER_SERVICE_MODULE).get(VEHICLE_POSITIONS_ENDPOINT, {
      vehicle_ids: vehicleIds.join(',')
    })

    return isArray(data) ? data : []
  }

  /**
   * The vehicle and the driver of one order.
   *
   * The listing reports the pair on the row itself; the details screen reports it under
   * the plan's snapshot. Both shapes reach this - the order's own map is fed the details
   * record - so both are read, the same way `buildIrregularityFilters` reads them.
   */
  const toVehicleAndDriver = (record) => ({
    vehicleId: record?.vehicle?.id ?? record?.task_data?.vehicle?.id ?? null,
    driverName: record?.driver?.full_name ?? record?.task_data?.driver?.full_name ?? null
  })

  /**
   * The vehicles of the given orders, as the map draws them. An order with no vehicle -
   * one that was never taken over - brings none.
   */
  const loadVehiclePositions = async (records) => {
    const orders = (isArray(records) ? records : [])
      .map(record => ({ record, ...toVehicleAndDriver(record) }))
      .filter(({ vehicleId }) => !!vehicleId)

    if (!orders.length) {
      return []
    }

    // One order per vehicle. The same vehicle can carry two orders in the window the
    // listing shows, and it still stands in one place - the newest order is the one it
    // is out on now, and the listing answers newest first.
    const orderByVehicleId = new Map()

    orders.forEach(order => {
      if (!orderByVehicleId.has(order.vehicleId)) {
        orderByVehicleId.set(order.vehicleId, order)
      }
    })

    try {
      isFetching.value = true

      const positions = await fetchPositions([...orderByVehicleId.keys()])

      return positions.map(position => {
        const order = orderByVehicleId.get(position?.vehicle_id)

        return {
          ...position,
          order_id: order?.record?.id ?? null,
          driver_name: order?.driverName || null
        }
      })
    }
    catch (exception) {
      console.error(exception)

      return []
    }
    finally {
      isFetching.value = false
    }
  }

  return {
    isFetching,
    loadVehiclePositions
  }
}
